import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Card, Tab, Badge } from "@mui/material";
import RecentVisits from "./RecentVisits";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import UserAnalytics from "./UserAnalytics";
import TasksAnalytics from "./TasksAnalytics";
import UserFeedback from "./UserFeedback";
import Messaging from "./Messaging";
import GlobalStats from "./GlobalStats";
import DeletionRequests from "./DeletionRequests";
import { useSelector, useDispatch } from "react-redux";
import useAdminAPIClient from "../../api/admin/hooks/useAdminAPIClient";
import {
  setAdminUsers,
  setAdminTasks,
  setAdminSessions,
  setAdminStats,
  setAdminFeedback,
  setAdminConversations,
  setAdminLoading,
} from "../../redux/actions/admin";

const AdminTabs = () => {
  const dispatch = useDispatch();
  const {
    getUsers,
    getTasks,
    getSessions,
    getStats,
    getFeedback,
    getConversations,
    getNotifications,
  } = useAdminAPIClient();

  const [activeTab, setActiveTab] = useState("1"); // Stats is now tab 1
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [conversationToView, setConversationToView] = useState(null);
  const [taskSearchTerm, setTaskSearchTerm] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState(null);

  const {
    conversations,
    feedback = [],
    loading,
    loaded,
  } = useSelector((state) => state.admin);

  // Track mounted state to prevent state updates after unmount
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Count unread messages from users
  const unreadMessageCount = useMemo(() => {
    let count = 0;
    Object.values(conversations || {}).forEach((messages) => {
      messages.forEach((msg) => {
        if (msg.senderType === "user" && !msg.read) {
          count++;
        }
      });
    });
    return count;
  }, [conversations]);

  // Count unread feedback
  const unreadFeedbackCount = useMemo(() => {
    return feedback.filter((f) => !f.read).length;
  }, [feedback]);

  // Count pending deletion requests
  const pendingDeletionCount = useMemo(() => {
    let count = 0;
    Object.values(conversations || {}).forEach((messages) => {
      messages.forEach((msg) => {
        if (msg.messageType === "deletion-request" && !msg.read) {
          count++;
        }
      });
    });
    return count;
  }, [conversations]);

  const handleSetMessageRecipient = (userId) => {
    setMessageRecipient(userId);
  };

  const handleSetConversationToView = (userId) => {
    setConversationToView(userId);
  };

  const handleSetTaskSearchTerm = (searchTerm) => {
    setTaskSearchTerm(searchTerm);
  };

  const handleSetUserSearchTerm = (searchTerm) => {
    setUserSearchTerm(searchTerm);
  };

  // Load notifications (feedback + conversations) on mount for badge counts
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const data = await getNotifications();
        if (isMounted.current && data) {
          if (data.feedback) dispatch(setAdminFeedback(data.feedback));
          if (data.conversations)
            dispatch(setAdminConversations(data.conversations));
        }
      } catch (error) {
        console.error("Failed to load notifications:", error);
      }
    };
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load tab-specific data when tab changes
  const loadTabData = useCallback(
    async (tabId) => {
      switch (tabId) {
        case "1": // Stats
          if (!loaded.stats) {
            dispatch(setAdminLoading("stats", true));
            const data = await getStats();
            if (isMounted.current && data?.analytics) {
              dispatch(setAdminStats(data.analytics));
            }
          }
          break;
        case "2": // Sessions
          if (!loaded.sessions) {
            dispatch(setAdminLoading("sessions", true));
            const data = await getSessions();
            if (isMounted.current && data?.recentVisits) {
              dispatch(setAdminSessions(data.recentVisits));
            }
          }
          break;
        case "3": // Users
          if (!loaded.users) {
            dispatch(setAdminLoading("users", true));
            const data = await getUsers();
            if (isMounted.current && data?.users) {
              dispatch(setAdminUsers(data.users));
            }
          }
          break;
        case "4": // Tasks
          if (!loaded.tasks) {
            dispatch(setAdminLoading("tasks", true));
            const data = await getTasks();
            if (isMounted.current && data?.tasks) {
              dispatch(setAdminTasks(data.tasks));
            }
          }
          break;
        // Feedback, Messaging, Deletions are loaded via notifications on mount
        default:
          break;
      }
    },
    [
      dispatch,
      getStats,
      getSessions,
      getUsers,
      getTasks,
      loaded.stats,
      loaded.sessions,
      loaded.users,
      loaded.tasks,
    ],
  );

  // Load data when tab changes
  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  // Poll for updates - only for notification tabs (Feedback, Messaging, Deletions)
  useEffect(() => {
    // Only poll when on Feedback (5), Messaging (6), or Deletions (7) tabs
    if (activeTab !== "5" && activeTab !== "6" && activeTab !== "7") {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        if (activeTab === "5") {
          // Poll feedback only
          const data = await getFeedback();
          if (isMounted.current && data?.feedback) {
            dispatch(setAdminFeedback(data.feedback));
          }
        } else if (activeTab === "6" || activeTab === "7") {
          // Poll conversations for messaging and deletions
          const data = await getConversations();
          if (isMounted.current && data?.conversations) {
            dispatch(setAdminConversations(data.conversations));
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 5000); // Poll every 5 seconds

    return () => {
      clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <Card sx={{ width: "100%", boxSizing: "border-box" }}>
      <TabContext value={activeTab}>
        <TabList
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            "& .MuiTabs-scrollButtons": {
              width: "auto",
            },
          }}
        >
          <Tab label="Stats" value={"1"} />
          <Tab label="Sessions" value={"2"} />
          <Tab label="Users" value={"3"} />
          <Tab label="Tasks" value={"4"} />
          <Tab
            label={
              <Badge badgeContent={unreadFeedbackCount} color="error" max={99}>
                Feedback
              </Badge>
            }
            value={"5"}
          />
          <Tab
            label={
              <Badge badgeContent={unreadMessageCount} color="error" max={99}>
                Messaging
              </Badge>
            }
            value={"6"}
          />
          <Tab
            label={
              <Badge badgeContent={pendingDeletionCount} color="error" max={99}>
                Deletions
              </Badge>
            }
            value={"7"}
          />
        </TabList>
        <TabPanel value="1">
          <GlobalStats />
        </TabPanel>
        <TabPanel value="2">
          <RecentVisits
            onSwitchTab={setActiveTab}
            onSetMessageRecipient={handleSetMessageRecipient}
            onSetUserSearchTerm={handleSetUserSearchTerm}
          />
        </TabPanel>
        <TabPanel value="3">
          <UserAnalytics
            onSwitchTab={setActiveTab}
            onSetMessageRecipient={handleSetMessageRecipient}
            onSetTaskSearchTerm={handleSetTaskSearchTerm}
            initialUserSearchTerm={userSearchTerm}
            onClearUserSearchTerm={() => setUserSearchTerm(null)}
          />
        </TabPanel>
        <TabPanel value="4">
          <TasksAnalytics
            onSwitchTab={setActiveTab}
            onSetMessageRecipient={handleSetMessageRecipient}
            initialSearchTerm={taskSearchTerm}
            onClearSearchTerm={() => setTaskSearchTerm(null)}
          />
        </TabPanel>
        <TabPanel value="5">
          <UserFeedback
            onSwitchTab={setActiveTab}
            onSetMessageRecipient={handleSetMessageRecipient}
            onSetConversationToView={handleSetConversationToView}
          />
        </TabPanel>
        <TabPanel value="6">
          <Messaging
            messageRecipient={messageRecipient}
            conversationToView={conversationToView}
            onClearRecipient={() => setMessageRecipient(null)}
            onClearConversationToView={() => setConversationToView(null)}
          />
        </TabPanel>
        <TabPanel value="7">
          <DeletionRequests
            onSwitchTab={setActiveTab}
            onSetTaskSearchTerm={handleSetTaskSearchTerm}
          />
        </TabPanel>
      </TabContext>
    </Card>
  );
};

export default AdminTabs;
